/* Daily Wear Diamonds — section reveal controller */
(() => {
  const selector = '#MainContent > .shopify-section:not(:first-child)';
  const prepare = (root = document) => {
    const sections = root.querySelectorAll?.(selector) || [];
    sections.forEach((section, index) => {
      if (section.dataset.dwdMotionReady) return;
      section.dataset.dwdMotionReady = 'true';
      section.classList.add('dwd-reveal');
      section.style.setProperty('--dwd-reveal-delay', Math.min(index % 3, 2) * 55 + 'ms');
    });
    if (matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      sections.forEach(section => section.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    sections.forEach(section => observer.observe(section));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => prepare(), { once: true });
  else prepare();
  document.addEventListener('shopify:section:load', event => prepare(event.target.ownerDocument));
})();

/* DWD featured products autoplay */
(() => {
  const AUTOPLAY_DELAY = 4500;
  const RESUME_DELAY = 6500;
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  function initFeaturedAutoplay(root) {
    if (root.dataset.dwdAutoplayReady) return;
    const viewport = root.querySelector('[data-casa-featured-viewport]');
    const track = viewport?.querySelector('.casa-featured__track');
    if (!viewport || !track) return;

    root.dataset.dwdAutoplayReady = 'true';
    let timer;
    let resumeTimer;
    let paused = false;

    const isScrollable = () => viewport.scrollWidth > viewport.clientWidth + 2;
    const getStep = () => {
      const card = track.querySelector('.casa-featured__column');
      if (!card) return Math.max(viewport.clientWidth * 0.8, 280);
      const style = getComputedStyle(track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const stop = () => {
      clearInterval(timer);
      timer = undefined;
    };

    const advance = () => {
      if (paused || document.hidden || reducedMotion.matches || !isScrollable()) return;
      const max = viewport.scrollWidth - viewport.clientWidth;
      const next = viewport.scrollLeft + getStep();
      viewport.scrollTo({ left: next >= max - 4 ? 0 : next, behavior: 'smooth' });
    };

    const start = () => {
      stop();
      if (!paused && !document.hidden && !reducedMotion.matches && isScrollable()) {
        timer = setInterval(advance, AUTOPLAY_DELAY);
      }
    };

    const pause = () => {
      paused = true;
      stop();
      clearTimeout(resumeTimer);
    };

    const resume = (delay = 0) => {
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(() => {
        paused = false;
        start();
      }, delay);
    };

    root.addEventListener('pointerenter', pause);
    root.addEventListener('pointerleave', () => resume(800));
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) resume(800);
    });
    viewport.addEventListener('pointerdown', pause, { passive: true });
    viewport.addEventListener('pointerup', () => resume(RESUME_DELAY), { passive: true });
    viewport.addEventListener('touchend', () => resume(RESUME_DELAY), { passive: true });
    document.addEventListener('visibilitychange', start);
    window.addEventListener('resize', start, { passive: true });
    reducedMotion.addEventListener?.('change', start);
    start();
  }

  const init = () => document.querySelectorAll('[data-casa-featured-carousel]').forEach(initFeaturedAutoplay);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* DWD cross-page anchor centering */
(() => {
  const centerHashTarget = () => {
    if (!window.location.hash) return;
    let target;
    try {
      target = document.querySelector(window.location.hash);
    } catch (error) {
      return;
    }
    if (!target) return;
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    requestAnimationFrame(() => {
      requestAnimationFrame(() => target.scrollIntoView({ behavior, block: 'center', inline: 'nearest' }));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', centerHashTarget, { once: true });
  } else {
    centerHashTarget();
  }
  window.addEventListener('load', centerHashTarget, { once: true });
  window.addEventListener('hashchange', centerHashTarget);
})();
